import { useTranslation } from 'react-i18next';
import { Link, Text } from '@hotjar/hotsauce';
import { trackClick } from '@hotjar/tracking';

import { useHelpHotjarUrl } from '@hotjar/translations';
import type { TSupportSecondaryView } from '../../../constants-types/SmartPanelSecondaryViews';
import { SMART_PANEL_SECONDARY_VIEWS } from '../../../constants-types/SmartPanelSecondaryViews';
import type { TrackableSite } from '../../../constants-types/ExternalDependencyTypes';
import { SupportSecondaryView } from './SupportSecondaryView';
import { EVENT_UI_POSITIONS } from '../../../constants-types/TrackEventUIPositions';
import { EVENT_NAMES } from '../../../constants-types/TrackEventNames';
import { getTrackGroupDataAndTriggeredOnState } from '../../../utils/trackingUtils';
import { isSinglePageSearch } from '../../../utils/isSinglePageSearch';
import { MANUAL_RETAKER_MODES } from '../../../constants-types/ManualRetakerModes';
import type { RetakerMode } from '../../../constants-types/ManualRetakerTypes';

interface SupportSecondaryViewsProps {
  activeView?: TSupportSecondaryView;
  currentSite?: TrackableSite;
  handleManualRetakerClick: (url: string, mode?: RetakerMode | undefined) => void;
  selectedTargetingValue: TargetingValue;
  originUrl: string | null;
}

export const SupportSecondaryViews = ({
  activeView,
  currentSite,
  handleManualRetakerClick,
  selectedTargetingValue,
  originUrl,
}: SupportSecondaryViewsProps): JSX.Element | null => {
  const { t } = useTranslation('heatmaps');
  const helpHotjarUrl = useHelpHotjarUrl();

  const isSinglePage = isSinglePageSearch(selectedTargetingValue);

  if (activeView === undefined) return null;

  const getTitle = (): string => {
    switch (activeView) {
      case SMART_PANEL_SECONDARY_VIEWS.HM_DOES_NOT_LOOK_RIGHT:
        return t('smart-panel.support.does-not-look-right');
      case SMART_PANEL_SECONDARY_VIEWS.HM_NOT_CAPTURING_URL:
        return t('smart-panel.support.not-capturing-right-url');
      case SMART_PANEL_SECONDARY_VIEWS.HM_SHOWS_COOKIES_BANNER:
        return t('smart-panel.support.cookies-banners');
      default:
        return '';
    }
  };

  const getVideoPath = (): string => {
    let videoPath = 'https://assets.hotjar.com/content/hm-sp-support-flow-b.mp4';

    if (activeView === SMART_PANEL_SECONDARY_VIEWS.HM_SHOWS_COOKIES_BANNER) {
      videoPath = 'https://assets.hotjar.com/content/hm-sp-support-flow-c.mp4';
    }

    return videoPath;
  };

  const getDescription = (): React.ReactNode => {
    switch (activeView) {
      case SMART_PANEL_SECONDARY_VIEWS.HM_DOES_NOT_LOOK_RIGHT:
        return (
          <>
            <Text>
              {t('smart-panel.support.does-not-look-right-description.most-common-issues')}
            </Text>
            <ul>
              <Text as="li">
                {t('smart-panel.support.does-not-look-right-description.issue-cut-off')}
              </Text>

              <Text as="li">
                {t('smart-panel.support.does-not-look-right-description.issue-missing-elements')}
              </Text>
              <Text as="li">
                {t('smart-panel.support.does-not-look-right-description.issue-white-spaces')}
              </Text>
              <Text as="li">
                {t('smart-panel.support.does-not-look-right-description.issue-mobile-elements')}
              </Text>
            </ul>
          </>
        );
      case SMART_PANEL_SECONDARY_VIEWS.HM_NOT_CAPTURING_URL:
        return <Text>{t('smart-panel.support.not-capturing-right-url-description')}</Text>;
      case SMART_PANEL_SECONDARY_VIEWS.HM_SHOWS_COOKIES_BANNER:
        return <Text>{t('smart-panel.support.cookies-banners-description')}</Text>;
      default:
        return null;
    }
  };

  const renderFixSuggestion = (withHelpLink = false) => {
    return (
      <Text textType="body2">
        {t('smart-panel.support.try')}{' '}
        <Text textType="subtitle2">{t('smart-panel.support.taking-a-screenshot')}</Text>
        {withHelpLink && (
          <>
            {t('smart-panel.support.read-about')}{' '}
            <Link
              href={`${helpHotjarUrl}/articles/5215291845143-How-to-Update-a-Heatmap-Screenshot`}
              kind="inline"
              isExternal
            >
              {t('smart-panel.support.screenshot-troubleshooting')}
            </Link>
          </>
        )}
      </Text>
    );
  };

  const getHowToFix = (): React.ReactNode => {
    switch (activeView) {
      case SMART_PANEL_SECONDARY_VIEWS.HM_DOES_NOT_LOOK_RIGHT:
      case SMART_PANEL_SECONDARY_VIEWS.HM_SHOWS_COOKIES_BANNER:
        return renderFixSuggestion(true);
      case SMART_PANEL_SECONDARY_VIEWS.HM_NOT_CAPTURING_URL:
        return renderFixSuggestion();
      default:
        return null;
    }
  };

  const onTakeNewScreenshotClick = () => {
    let retakerMode: RetakerMode = MANUAL_RETAKER_MODES.basicScreenshot;

    if (activeView === SMART_PANEL_SECONDARY_VIEWS.HM_SHOWS_COOKIES_BANNER) {
      retakerMode = MANUAL_RETAKER_MODES.cookiesBanners;
    }

    handleManualRetakerClick(
      isSinglePage && originUrl !== null && originUrl.length > 0 ? originUrl : '',
      retakerMode,
    );

    if (currentSite !== undefined) {
      let elementName = EVENT_NAMES.HM_DOES_NOT_LOOK_RIGHT_RETAKER;

      if (activeView === SMART_PANEL_SECONDARY_VIEWS.HM_NOT_CAPTURING_URL) {
        elementName = EVENT_NAMES.HM_NOT_CAPTURING_URL_RETAKER;
      }

      if (activeView === SMART_PANEL_SECONDARY_VIEWS.HM_SHOWS_COOKIES_BANNER) {
        elementName = EVENT_NAMES.HM_SHOWS_COOKIES_BANNER_RETAKER;
      }

      trackClick({
        name: elementName,
        uiPosition: EVENT_UI_POSITIONS.SMART_PANEL,
        ...getTrackGroupDataAndTriggeredOnState(currentSite),
      });
    }
  };

  return (
    <SupportSecondaryView
      titleText={getTitle()}
      videoPath={getVideoPath()}
      description={getDescription()}
      howToFix={getHowToFix()}
      onTakeNewScreenshotClick={onTakeNewScreenshotClick}
    />
  );
};
